/**
 * AppSidebar — desktop primary navigation. Wordmark top, destinations grouped
 * (Overview / Daily / Setup) from the sections registry, the active item
 * carrying its section accent (tint bg + left accent bar + section-coloured
 * icon/label), and the user/PIN affordance pinned at the bottom.
 */
import { NavLink } from 'react-router-dom';
import { useWhoami } from '../lib/auth';
import { SECTION_GROUPS } from './sections';
import { UserMenu, PinUnlockedBadge } from './UserMenu';

export const SIDEBAR_WIDTH = 212;

export function AppSidebar() {
  const who = useWhoami();
  const isBoss = who.data?.state === 'authenticated' && who.data.role === 'boss';
  const pinUntil =
    who.data?.state === 'authenticated' ? who.data.pinUnlockedUntil : null;

  return (
    <aside
      className="fixed inset-y-0 left-0 z-30 hidden flex-col md:flex"
      style={{
        width: SIDEBAR_WIDTH,
        background: 'var(--sidebar-bg)',
        borderRight: '1px solid var(--sidebar-border)',
      }}
    >
      <div
        className="flex h-14 shrink-0 items-center gap-2 px-4"
        style={{ borderBottom: '1px solid var(--sidebar-border)' }}
      >
        <span className="text-body font-semibold tracking-tight text-white">
          SRB <span style={{ color: 'var(--sidebar-accent)' }}>Accounts</span>
        </span>
      </div>

      <nav className="flex-1 overflow-y-auto px-2 py-3">
        {SECTION_GROUPS.map((group) => (
          <div key={group.label} className="mb-3">
            <div
              className="px-2.5 pb-1 text-[11px] font-semibold uppercase tracking-wider"
              style={{ color: 'var(--sidebar-muted)' }}
            >
              {group.label}
            </div>
            {group.items.map((item) => {
              const Icon = item.icon;
              return (
                <NavLink
                  key={item.key}
                  to={item.to}
                  end={item.to === '/'}
                  className="group flex min-h-[40px] items-center gap-2.5 rounded-md border-l-2 border-transparent px-2.5 py-2 text-small font-medium transition-colors hover:bg-[#16273e]"
                  style={({ isActive }) =>
                    isActive
                      ? { background: 'var(--sidebar-raised)', borderLeftColor: item.color }
                      : undefined
                  }
                >
                  {({ isActive }) => (
                    <>
                      <span
                        style={{ color: isActive ? item.color : 'var(--sidebar-muted)' }}
                        aria-hidden
                      >
                        <Icon className="h-[18px] w-[18px]" strokeWidth={2} />
                      </span>
                      <span
                        className={isActive ? 'font-semibold' : 'group-hover:text-white'}
                        style={{ color: isActive ? '#ffffff' : 'var(--sidebar-text)' }}
                      >
                        {item.label}
                      </span>
                    </>
                  )}
                </NavLink>
              );
            })}
          </div>
        ))}
      </nav>

      <div
        className="shrink-0 space-y-1.5 p-2"
        style={{ borderTop: '1px solid var(--sidebar-border)' }}
      >
        {isBoss && (
          <span className="inline-flex rounded-md bg-amber-400/15 px-2 py-0.5 text-caption font-medium text-amber-300 ring-1 ring-amber-400/30">
            read-only
          </span>
        )}
        {pinUntil && (
          <div>
            <PinUnlockedBadge unlockedUntil={pinUntil} />
          </div>
        )}
        <UserMenu align="left" tone="dark" />
      </div>
    </aside>
  );
}
